///
/// EarthBrowser
///
/// Created by Matt Giger on 9/17/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Globe layer for user placemarks. Unlike the other dataset layers this
/// one is fed from the redux placemarks slice (user state) rather than
/// the synced dataset SQLite tables, so there is no sync config and no
/// viewport querying - a user has tens of placemarks, not thousands, and
/// every one exists as a scene node while the layer is visible.
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { type Placemark } from "@/redux/placemarksSlice"
import { DatasetLayer, type DatasetFeature, type DatasetSceneObject } from "../DatasetLayer"
import { PlacemarkNode } from "./PlacemarkNode"

export interface PlacemarkFeature extends DatasetFeature {
	id: string
	placemark: Placemark
}

export class PlacemarksLayer extends DatasetLayer<PlacemarkFeature> {
	private unsubscribeRedux?: () => void
	private lastPlacemarks?: Record<string, Placemark>
	private lastPreview: Placemark | null = null
	private lastVisibleState = true

	constructor(context: EarthBrowserContext) {
		super(context, "placemarks")
		this.subscribeToRedux()
	}

	private subscribeToRedux(): void {
		const apply = () => {
			const state = engineBridge.getState()
			if (!state) return

			const isVisible = state.earthbrowser.visibleDatasets.includes("placemark")
			if (isVisible !== this.lastVisibleState) {
				this.lastVisibleState = isVisible
				this.visible = isVisible
			}

			// Immer replaces the record reference on any placemark change,
			// so a reference check is enough to skip no-op store updates.
			// A share-link preview placemark (dialog slice, ephemeral)
			// renders alongside the user's own without entering their data.
			const placemarks = state.placemarks.placemarks
			const preview = state.dialog.sharedPlacemarkPreview
			if (placemarks !== this.lastPlacemarks || preview !== this.lastPreview) {
				this.lastPlacemarks = placemarks
				this.lastPreview = preview
				this.updateSceneObjects(this.toFeatures(placemarks, preview))
			}
		}

		apply()
		this.unsubscribeRedux = engineBridge.subscribe(apply)
	}

	private toFeatures(placemarks: Record<string, Placemark>, preview: Placemark | null): PlacemarkFeature[] {
		const features = Object.values(placemarks).map(placemark => ({ id: placemark.id, placemark }))
		// "Add to My Places" copies the preview into the user's placemarks
		// under the same id, at which point the real one takes over
		if (preview && !placemarks[preview.id]) {
			features.push({ id: preview.id, placemark: preview })
		}
		return features
	}

	public createSceneObject(feature: PlacemarkFeature): DatasetSceneObject<PlacemarkFeature> | undefined {
		return new PlacemarkNode(this.context, feature)
	}

	/** Rebuild from redux (the base refresh queries the dataset DB, which
	 *  placemarks don't live in) */
	public override async refresh(): Promise<void> {
		const state = engineBridge.getState()
		if (!state) return
		const placemarks = state.placemarks.placemarks
		this.lastPlacemarks = placemarks
		this.lastPreview = state.dialog.sharedPlacemarkPreview
		this.updateSceneObjects(this.toFeatures(placemarks, this.lastPreview))
	}

	/** Deep links (?target=pin-<uuid> or pin-<uuid>:<memberId>): placemarks
	 *  are always in redux, so a missing id simply doesn't exist (deleted or
	 *  another account). A member suffix resolves to the live member pin when
	 *  the placemark is expanded (keeping the URL-target round trip idempotent:
	 *  focusing a member pushes pin-<uuid>:<memberId>, which must resolve back
	 *  to that same node id or the anchor steals focus); otherwise the anchor
	 *  node aims its focus framing at that one member. */
	public override async loadSceneObjectById(id: string): Promise<DatasetSceneObject<PlacemarkFeature> | undefined> {
		const separator = id.indexOf(":")
		const placemarkId = separator >= 0 ? id.slice(0, separator) : id
		const memberId = separator >= 0 ? id.slice(separator + 1) : undefined

		// getSceneObject builds the node on demand if the layer is hidden
		const node = this.getSceneObject(placemarkId)
		if (node instanceof PlacemarkNode) {
			if (memberId !== undefined) {
				const memberNode = node.getMemberNode(memberId)
				if (memberNode) return memberNode
			}
			node.setFocusMember(memberId)
		}
		return node
	}

	override destroy(): void {
		this.unsubscribeRedux?.()
		super.destroy()
	}
}
